/**
 * Answer-inline "Try it yourself" (LOOM_LIVE L-2). Decision: the run is triggered
 * from the answer (ai-native "runnable answer"), not a session-level toggle — see
 * docs/design/2026-06-26-loom-live-entry-options.html.
 *
 * TryItYourself: shows a CTA under a settled answer ONLY when the user's 织灵 is
 * connected; click → an in-place confirm (authorization) → mounts LiveRunBlock.
 * LiveRunBlock: streams /api/loom/run and renders the live ADE session by folding
 * the SSE frames into loom MessageBlock[] and reusing the replay BlockRenderer —
 * so a live run looks exactly like a recorded replay, just growing.
 */
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import type { MessageBlock } from "@shared/loom-message-block";
import { loomApi } from "../../lib/loomApi";
import { foldAcpUpdate, type AcpUpdate } from "./acp-to-blocks";
import { BlockRenderer } from "../chat/loom-replay/block-renderer";
import "../../styles/loom.css";

type RunPhase = "running" | "done" | "error" | "stopped";

/**
 * Streams one run and renders its blocks live (reusing the replay views). When the
 * turn context (conversationId + responseId) is present, it ALSO persists the run
 * via loomApi.record (running → done/error) so a reload re-renders it from
 * turn.blocks (see openspec/loom-live-persistence.md).
 */
export function LiveRunBlock({ prompt, conversationId }: { prompt: string; conversationId?: string }): JSX.Element {
  const [blocks, setBlocks] = useState<MessageBlock[]>([]);
  // Completed prior rounds (follow-ups #4 accumulate, not replace). Each carries
  // its prompt so the card reads like a self-contained multi-round transcript.
  const [rounds, setRounds] = useState<{ prompt: string; blocks: MessageBlock[] }[]>([]);
  const [phase, setPhase] = useState<RunPhase>("running");
  const [reason, setReason] = useState<"setup" | "exec" | "not_connected">("exec");
  const [elapsed, setElapsed] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const startedAttempt = useRef(-1);
  const loomSid = useRef("");
  const blocksRef = useRef<MessageBlock[]>([]);
  const roundsRef = useRef<{ prompt: string; blocks: MessageBlock[] }[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const stoppedRef = useRef(false);
  // The prompt for the current round + whether to reuse the existing loom session
  // (follow-up #4). Refs so changing them doesn't re-fire the attempt-keyed effect.
  const promptRef = useRef(prompt);
  const reuseSidRef = useRef(false);
  const [followUp, setFollowUp] = useState("");

  // Persist a lifecycle transition, keyed by the loom session id (available once
  // onSession fires). Needs the conversation context only. Hoisted so stop() can call it.
  const record = (status: RunPhase, snapshot?: MessageBlock[]): void => {
    if (conversationId === undefined || loomSid.current === "") return;
    // Snapshot = all completed rounds' blocks + the current round's (so a reload
    // shows the full multi-round run, not just the last round).
    const fullSnapshot =
      snapshot !== undefined ? [...roundsRef.current.flatMap((r) => r.blocks), ...snapshot] : undefined;
    void loomApi.record({
      conversationId,
      loomSessionId: loomSid.current,
      status,
      prompt: promptRef.current,
      ...(fullSnapshot !== undefined ? { blocks: fullSnapshot } : {}),
    });
  };

  // Stop the run (#1): cancel the agent task in loom + abort the stream.
  const stop = (): void => {
    stoppedRef.current = true;
    if (loomSid.current !== "") void loomApi.cancel(loomSid.current);
    abortRef.current?.abort();
    setPhase("stopped");
    record("stopped", blocksRef.current);
  };

  // Retry a failed run with a FRESH session (the prior one failed).
  const retry = (): void => {
    promptRef.current = prompt;
    reuseSidRef.current = false;
    loomSid.current = "";
    setBlocks([]);
    blocksRef.current = [];
    setElapsed(0);
    setPhase("running");
    setAttempt((a) => a + 1);
  };

  // Continue the SAME loom session with another prompt (#4). The session keeps
  // context; the finished round is archived into `rounds` (kept visible) and a new
  // round starts — follow-ups accumulate like a conversation, never replace.
  const continueRun = (): void => {
    const text = followUp.trim();
    if (text === "" || loomSid.current === "") return;
    const finished = { prompt: promptRef.current, blocks: blocksRef.current };
    roundsRef.current = [...roundsRef.current, finished];
    setRounds((rs) => [...rs, finished]);
    promptRef.current = text;
    reuseSidRef.current = true;
    setFollowUp("");
    setBlocks([]);
    blocksRef.current = [];
    setElapsed(0);
    setPhase("running");
    setAttempt((a) => a + 1);
  };

  // Elapsed timer while running, so the wait (esp. the session-create gap before
  // the first event) reads as alive rather than stuck.
  useEffect(() => {
    if (phase !== "running") return undefined;
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (startedAttempt.current === attempt) return; // run each attempt once
    startedAttempt.current = attempt;
    stoppedRef.current = false;
    const ac = new AbortController();
    abortRef.current = ac;
    const reuse = reuseSidRef.current ? loomSid.current : undefined;
    loomApi
      .run(
        promptRef.current,
        (event, data) => {
          if (event === "session") {
            loomSid.current = (data as { sessionId?: string }).sessionId ?? "";
            record("running");
          } else if (event === "update") {
            setBlocks((prev) => {
              const next = foldAcpUpdate(prev, data as AcpUpdate);
              blocksRef.current = next;
              return next;
            });
          } else if (event === "end") {
            setPhase("done");
            record("done", blocksRef.current);
          } else if (event === "error") {
            const d = data as { message?: string; stage?: string };
            setReason(d.message === "not_connected" ? "not_connected" : d.stage === "setup" ? "setup" : "exec");
            setPhase("error");
            record("error");
          }
        },
        { signal: ac.signal, ...(reuse !== undefined ? { loomSessionId: reuse } : {}) },
      )
      .then(() => setPhase((p) => (p === "error" || stoppedRef.current ? p : "done")))
      .catch(() => {
        if (!stoppedRef.current) setPhase("error"); // an abort is a stop, not an error
      });
  }, [attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const head =
    phase === "running"
      ? "正在你的织灵里执行…"
      : phase === "done"
        ? "已完成"
        : phase === "stopped"
          ? "已停止"
          : reason === "not_connected"
            ? "还没连接织灵账号"
            : "织灵这次没跑完";

  // The rendered run blocks: completed prior rounds (follow-ups accumulate) each
  // with their prompt, then the current round. replay-block-card provides the loom
  // block CSS (color vars + icon sizing) — without it the views render unstyled.
  const body = (
    <div className="loom-runbody replay-block-card">
      {rounds.map((r, ri) => (
        <div key={`round-${ri}`} className="loom-round">
          <div className="loom-round-q">{r.prompt}</div>
          {r.blocks.map((b) => (
            <BlockRenderer key={b.id} block={b} />
          ))}
        </div>
      ))}
      {rounds.length > 0 && <div className="loom-round-q">{promptRef.current}</div>}
      {blocks.map((b) => (
        <BlockRenderer key={b.id} block={b} />
      ))}
    </div>
  );

  return (
    <div className="loom-runcard">
      <div className="loom-runhead">
        <span className={`loom-rundot ${phase}`} />
        {head}
        {phase === "running" && elapsed > 1 && <span className="loom-run-elapsed">{elapsed}s</span>}
        {phase === "running" && (
          <button type="button" className="loom-btn ghost loom-run-stop" onClick={stop}>
            停止
          </button>
        )}
      </div>
      {phase === "error" && reason === "not_connected" ? (
        <div className="loom-runnote">右上角点一下织灵图标连上,再回来试跑。</div>
      ) : phase === "error" ? (
        <div className="loom-runnote">
          {reason === "setup"
            ? "没能在你的织灵里把这件事跑起来(可能是织灵那边暂时连不上或忙)。这不影响小环答题。"
            : "你的织灵接到了任务,但这次没跑完(织灵那边的临时问题,比如执行超时)。这不影响小环答题。"}
          <button type="button" className="loom-btn ghost loom-run-retry" onClick={retry}>
            重试
          </button>
        </div>
      ) : (
        // replay-block-card provides the loom block CSS (color vars + icon sizing);
        // without it the ported block views render unstyled (icons blow up).
        body
      )}
      {/* Follow-up (#4): continue the same loom session after it settles. */}
      {(phase === "done" || phase === "stopped") && loomSid.current !== "" && (
        <div className="loom-followup">
          <input
            className="loom-in"
            value={followUp}
            placeholder="接着问一句,在同一个会话里继续…"
            onChange={(e) => setFollowUp(e.target.value)}
            onKeyDown={(e) => {
              // Ignore Enter while an IME composition is active (confirming a
              // Chinese candidate with space/Enter must not submit). Mirrors Composer.
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === "Enter") continueRun();
            }}
          />
          <button type="button" className="loom-btn" disabled={followUp.trim() === ""} onClick={continueRun}>
            继续
          </button>
        </div>
      )}
    </div>
  );
}

/** Module-cached connection flag, refreshed on the panel's loom-conn-changed event. */
let connectedCache: boolean | null = null;

/**
 * Whether the user's 织灵 is connected (null until first known). Module-cached +
 * refreshed on the panel's `loom-conn-changed` event, so every connection-gated
 * affordance — the answer-inline CTA AND the replay play-bar's Try it yourself —
 * stays live without each fetching independently.
 */
export function useLoomConnected(): boolean | null {
  const [connected, setConnected] = useState<boolean | null>(connectedCache);
  useEffect(() => {
    let on = true;
    const refresh = (): void => {
      loomApi
        .status()
        .then((s) => {
          connectedCache = s.connected;
          if (on) setConnected(s.connected);
        })
        .catch(() => {
          /* not logged in / offline — treat as not connected */
        });
    };
    if (connectedCache === null) refresh();
    window.addEventListener("loom-conn-changed", refresh);
    return () => {
      on = false;
      window.removeEventListener("loom-conn-changed", refresh);
    };
  }, []);
  return connected;
}

/**
 * The answer-inline CTA + confirm gate. Shown ONLY when (1) the user's 织灵 is
 * connected AND (2) the model marked this answer `runnable` via the `respond` tool
 * (single source of truth — the same model writes a coherent "I can run this"
 * answer). Replaces the former side-model classifier: no extra per-answer call.
 */
export function TryItYourself({
  prompt,
  runnable,
  conversationId,
}: {
  prompt: string;
  runnable: boolean;
  conversationId?: string;
}): JSX.Element | null {
  const connected = useLoomConnected();
  const [phase, setPhase] = useState<"idle" | "confirm" | "run">("idle");

  if (connected !== true) return null; // only offer the run when the user is connected
  if (phase === "idle" && !runnable) return null; // gated on the model's runnable verdict
  if (phase === "run")
    return <LiveRunBlock prompt={prompt} {...(conversationId !== undefined ? { conversationId } : {})} />;
  if (phase === "confirm") {
    const shown = prompt.length > 64 ? `${prompt.slice(0, 64)}…` : prompt;
    return (
      <div className="loom-confirm">
        <div className="loom-confirm-t">将在你的织灵里执行</div>
        <div className="loom-confirm-d">用你自己的账号跑这一句:{shown}</div>
        <div className="loom-confirm-btns">
          <button className="loom-btn" type="button" onClick={() => setPhase("run")}>
            开始
          </button>
          <button className="loom-btn ghost" type="button" onClick={() => setPhase("idle")}>
            取消
          </button>
        </div>
      </div>
    );
  }
  return (
    <button className="loom-try" type="button" onClick={() => setPhase("confirm")}>
      在你的织灵里试跑这个 <span className="loom-try-arr">→</span>
    </button>
  );
}
